import { withCsrfHeaders } from 'utils/apiInterceptor';

const STORAGE_KEY = 'gevme-integration-login';
const PENDING_KEY = 'gevme-integration-login-pending';

const FIELDS = [
    'company_id',
    'client_id',
    'client_name',
    'app_name',
    'redirect_uri',
    'response_type',
    'state',
    'code_challenge',
    'code_challenge_method',
    'resource',
    'scope',
    'iss',
];

const ICONS = '/assets/integrations';

const CLIENTS = [
    {
        name: 'ChatGPT',
        icon: `${ICONS}/chatgpt.svg`,
        hosts: ['chatgpt.com', 'chat.openai.com', 'openai.com'],
        words: ['chatgpt', 'openai'],
    },
    {
        name: 'Claude',
        icon: `${ICONS}/claude.svg`,
        hosts: ['claude.ai', 'claude.com', 'anthropic.com'],
        words: ['claude', 'anthropic'],
    },
    {
        name: 'Cursor',
        icon: `${ICONS}/cursor.svg`,
        hosts: ['cursor.com'],
        schemes: ['cursor:'],
        words: ['cursor'],
    },
    {
        name: 'VS Code',
        icon: `${ICONS}/vscode.svg`,
        hosts: ['code.visualstudio.com'],
        schemes: ['vscode:', 'vscode-insiders:'],
        words: ['vscode', 'visual studio', 'visualstudio'],
    },
    {
        name: 'Windsurf',
        icon: `${ICONS}/windsurf.svg`,
        hosts: ['windsurf.com', 'codeium.com'],
        schemes: ['windsurf:'],
        words: ['windsurf', 'codeium'],
    },
];

function hostIs(url, hosts) {
    if (!url?.hostname) return false;
    return hosts.some(
        (host) => url.hostname === host || url.hostname.endsWith(`.${host}`)
    );
}

function parseUrl(value) {
    try {
        return new URL(value);
    } catch (error) {
        return null;
    }
}

function isLoopback(url) {
    if (!url?.hostname) return false;
    const host = url.hostname;
    return (
        host === 'localhost' ||
        host.endsWith('.localhost') ||
        host === '::1' ||
        host === '[::1]' ||
        /^127(\.\d{1,3}){3}$/.test(host)
    );
}

function mentions(value, words) {
    const text = String(value || '').toLowerCase();
    return words.some((word) => {
        const pattern = word.replace(/ /g, '\\s+');
        return new RegExp(`(^|[^a-z])${pattern}([^a-z]|$)`).test(text);
    });
}

// A loopback redirect must never become the title. "127.0.0.1" and "localhost"
// are addresses, not client names.
function readableName(value) {
    const name = String(value || '')
        .replace(/\s+/g, ' ')
        .trim();
    if (!name || name.length > 48) return '';
    if (/[\u0000-\u001f]/.test(name)) return '';
    if (
        /^(localhost|127(\.\d{1,3}){3}|\[?::1\]?)$/i.test(name) ||
        /\b127\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/.test(name) ||
        /\blocalhost\b/i.test(name)
    ) {
        return '';
    }
    return name;
}

function knownClient(values, redirect, clientIdUrl) {
    const opaqueId = clientIdUrl ? '' : values?.client_id;
    return CLIENTS.find((client) => {
        if (redirect && client.schemes?.includes(redirect.protocol))
            return true;
        if (redirect && hostIs(redirect, client.hosts)) return true;
        if (clientIdUrl && hostIs(clientIdUrl, client.hosts)) return true;
        return (
            mentions(values?.client_name, client.words) ||
            mentions(values?.app_name, client.words) ||
            mentions(opaqueId, client.words)
        );
    });
}

export function clientFromIntegration(values) {
    const redirect = parseUrl(values?.redirect_uri);
    const clientIdUrl = parseUrl(values?.client_id);
    const named =
        readableName(values?.client_name) || readableName(values?.app_name);
    const known = knownClient(values, redirect, clientIdUrl);

    if (known) {
        return { kind: 'known', name: known.name, icons: [known.icon] };
    }

    if (redirect && isLoopback(redirect)) {
        return {
            kind: 'local',
            name: named || 'a local app',
            icons: [`${ICONS}/local-app.svg`],
        };
    }

    if (
        !redirect ||
        (redirect.protocol !== 'https:' && redirect.protocol !== 'http:')
    ) {
        return {
            kind: 'app',
            name: named || 'your app',
            icons: [`${ICONS}/app.svg`],
        };
    }

    const host = redirect.hostname.replace(/^www\./, '');
    return {
        kind: 'web',
        name: named || host,
        icons: [`${redirect.origin}/apple-touch-icon.png`, `${ICONS}/app.svg`],
    };
}

export function readAuthorizeSearch(search) {
    const params = new URLSearchParams(search || '');
    const values = {};
    FIELDS.forEach((key) => {
        values[key] = params.get(key) || '';
    });
    if (!values.code_challenge_method) values.code_challenge_method = 'S256';
    return values;
}

export function isIntegrationQuery(values) {
    return Boolean(values?.client_id && values?.redirect_uri);
}

export function encodeIntegrationState(values) {
    return btoa(JSON.stringify(values))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

export function decodeIntegrationState(value) {
    if (!value) return null;
    try {
        const pad = value.replace(/-/g, '+').replace(/_/g, '/');
        const data = JSON.parse(atob(pad));
        return isIntegrationQuery(data) ? data : null;
    } catch (error) {
        return null;
    }
}

export function saveIntegrationLogin(values) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
}

export function loadIntegrationLogin() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        return null;
    }
}

export function hasIntegrationLogin() {
    return isIntegrationQuery(loadIntegrationLogin());
}

export function armIntegrationLogin() {
    if (hasIntegrationLogin()) sessionStorage.setItem(PENDING_KEY, '1');
}

export function integrationArmed() {
    return sessionStorage.getItem(PENDING_KEY) === '1' && hasIntegrationLogin();
}

export function dropIdleIntegrationLogin() {
    if (!integrationArmed()) sessionStorage.removeItem(STORAGE_KEY);
}

export function integrationReturnPath() {
    const values = loadIntegrationLogin();
    if (!isIntegrationQuery(values)) return '/login';
    const params = new URLSearchParams();
    FIELDS.forEach((key) => {
        if (values[key]) params.set(key, values[key]);
    });
    return `/authorize?${params.toString()}`;
}

export function rememberGoogleReturn(search) {
    const params = new URLSearchParams(search || '');
    const values = decodeIntegrationState(params.get('state'));
    if (values) saveIntegrationLogin(values);
    return values;
}

function clearIntegrationLogin() {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(PENDING_KEY);
}

export async function completeIntegrationLogin() {
    const params = loadIntegrationLogin();
    if (!params?.company_id || !params.redirect_uri || !params.code_challenge) {
        throw new Error('This connection link is invalid.');
    }
    const response = await fetch(
        `${
            process.env.REACT_APP_MYXP_API
        }/mcp/oauth/companies/${encodeURIComponent(params.company_id)}/grant`,
        {
            method: 'POST',
            credentials: 'include',
            headers: withCsrfHeaders({
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
                redirect_uri: params.redirect_uri,
                code_challenge: params.code_challenge,
                code_challenge_method: params.code_challenge_method || 'S256',
            }),
        }
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(
            body.error_description ||
                body.message ||
                'Could not connect this account.'
        );
    }
    const target = new URL(params.redirect_uri);
    target.searchParams.set('code', body.code);
    if (params.state) target.searchParams.set('state', params.state);
    if (params.iss) target.searchParams.set('iss', params.iss);
    clearIntegrationLogin();
    window.location.assign(target.toString());
}
